import { useState } from "react";
import { X, Lock, FileUp } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import RequestCredentialUpdateModal from "@/components/RequestCredentialUpdateModal";

const STATUS_LABELS = {
  valid: "Valid",
  expiring: "Expiring Soon",
  expired: "Expired",
  pending_review: "Pending Review",
};

const CREDENTIAL_TYPES = ["License", "Certification", "Safety Training", "Training Record"];

/**
 * mode: "add" | "edit"
 * "add" collects full credential detail from the worker — Status and
 * Verification are NOT worker-controllable and are server-defaulted to
 * "pending_review" / "Pending Verification".
 * "edit" keeps every verified field read-only; only notes are editable.
 */
export default function AddCredentialModal({
  mode = "add",
  workerId,
  credential,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit" && credential;

  const [showRequestUpdate, setShowRequestUpdate] = useState(false);
  const [form, setForm] = useState({
    name: credential?.name ?? "",
    issuer: credential?.issuer ?? "",
    expires: credential?.expires ?? "",
    notes: credential?.notes ?? "",
    credential_type: credential?.credential_type ?? "",
    license_number: credential?.license_number ?? "",
    state_jurisdiction: credential?.state_jurisdiction ?? "",
    trade: credential?.trade ?? "",
    issue_date: credential?.issue_date ?? "",
    supporting_document: credential?.supporting_document ?? "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && (!form.name || !form.issuer || !form.expires)) {
      toast.error("Credential name, issuer, and expiration date are required");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/credentials/${credential.id}`, { notes: form.notes || null });
        toast.success("Notes updated");
      } else {
        // Only send fields the API accepts. Status + verification are enforced server-side.
        const payload = {
          name: form.name,
          issuer: form.issuer,
          expires: form.expires,
          notes: form.notes || null,
          credential_type: form.credential_type || null,
          license_number: form.license_number || null,
          state_jurisdiction: form.state_jurisdiction || null,
          trade: form.trade || null,
          issue_date: form.issue_date || null,
          supporting_document: form.supporting_document || null,
        };
        await api.post(`/workers/${workerId}/credentials`, payload);
        toast.success("Credential submitted for verification", {
          description: "It will appear as Pending Review until an admin approves it.",
        });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save credential");
    } finally {
      setSubmitting(false);
    }
  };

  const readOnlyInput = "mt-1 w-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700";
  const editableInput = "mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#0B1221] focus:outline-none";

  return (
    <div
      data-testid={isEdit ? "edit-credential-modal" : "add-credential-modal"}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1221]/70 p-6"
      onClick={onClose}
    >
      <div
        className="relative max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="ac-close"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center text-slate-400 hover:text-[#0B1221]"
          type="button"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
          {isEdit ? "Update record" : "Submit for verification"}
        </div>
        <h3 className="font-display mt-1 text-2xl font-bold tracking-tight text-[#0B1221]">
          {isEdit ? "Edit Credential" : "Submit Credential"}
        </h3>

        {isEdit && (
          <div
            data-testid="ownership-notice"
            className="mt-4 flex items-start gap-2 border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-snug text-slate-700"
          >
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#FF5A1F]" strokeWidth={2.25} />
            <span>
              Workers own their wallet, not verified credential data. You can edit personal
              notes. To change credential details, submit an updated credential for verification.
            </span>
          </div>
        )}

        {!isEdit && (
          <div
            data-testid="submission-notice"
            className="mt-4 flex items-start gap-2 border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs leading-snug text-slate-700"
          >
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#FF5A1F]" strokeWidth={2.25} />
            <span>
              New credentials are submitted as <strong>Pending Review</strong> / <strong>Pending Verification</strong> and reviewed by a ProCred verifier before they count for job-site clearance.
            </span>
          </div>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Credential name {isEdit && <span className="ml-1 text-slate-400">(read-only)</span>}
              </label>
              <input
                data-testid="ac-name"
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. OSHA 30 Construction"
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
                required={!isEdit}
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Credential type
              </label>
              {isEdit ? (
                <input data-testid="ac-type" type="text" value={form.credential_type || ""} readOnly className={readOnlyInput} />
              ) : (
                <select
                  data-testid="ac-type"
                  value={form.credential_type}
                  onChange={(e) => set("credential_type", e.target.value)}
                  className={editableInput}
                >
                  <option value="">Select…</option>
                  {CREDENTIAL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Trade
              </label>
              <input
                data-testid="ac-trade"
                type="text"
                value={form.trade}
                onChange={(e) => set("trade", e.target.value)}
                placeholder="e.g. Electrical"
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
              />
            </div>

            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Issuer {isEdit && <span className="ml-1 text-slate-400">(read-only)</span>}
              </label>
              <input
                data-testid="ac-issuer"
                type="text"
                value={form.issuer}
                onChange={(e) => set("issuer", e.target.value)}
                placeholder="e.g. OSHA Authorized Trainer"
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
                required={!isEdit}
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                License / Cert #
              </label>
              <input
                data-testid="ac-license-number"
                type="text"
                value={form.license_number}
                onChange={(e) => set("license_number", e.target.value)}
                placeholder="e.g. WA-EL01-284591"
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                State / Jurisdiction
              </label>
              <input
                data-testid="ac-state"
                type="text"
                value={form.state_jurisdiction}
                onChange={(e) => set("state_jurisdiction", e.target.value)}
                placeholder="e.g. Washington"
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Issue date
              </label>
              <input
                data-testid="ac-issue-date"
                type="date"
                value={form.issue_date}
                onChange={(e) => set("issue_date", e.target.value)}
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
              />
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Expiration date {isEdit && <span className="ml-1 text-slate-400">(read-only)</span>}
              </label>
              <input
                data-testid="ac-expires"
                type="date"
                value={form.expires}
                onChange={(e) => set("expires", e.target.value)}
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
                required={!isEdit}
              />
            </div>

            <div className="col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Supporting document (filename)
              </label>
              <input
                data-testid="ac-doc"
                type="text"
                value={form.supporting_document}
                onChange={(e) => set("supporting_document", e.target.value)}
                placeholder="e.g. osha30_completion_cert.pdf"
                className={isEdit ? readOnlyInput : editableInput}
                readOnly={isEdit}
              />
            </div>

            {isEdit && (
              <>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Status <span className="ml-1 text-slate-400">(read-only)</span>
                  </label>
                  <input data-testid="ac-status" type="text" value={STATUS_LABELS[form.status] || credential?.status || ""} readOnly className={readOnlyInput} />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Verification <span className="ml-1 text-slate-400">(read-only)</span>
                  </label>
                  <input data-testid="ac-verification" type="text" value={credential?.verification || ""} readOnly className={readOnlyInput} />
                </div>
              </>
            )}
          </div>

          {isEdit && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Personal notes
              </label>
              <textarea
                data-testid="ac-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Add a personal note — visible only to you."
                rows={3}
                className={editableInput + " resize-none"}
              />
            </div>
          )}

          {isEdit && (
            <button
              type="button"
              data-testid="request-credential-update-btn"
              onClick={() => setShowRequestUpdate(true)}
              className="inline-flex w-full items-center justify-center gap-2 border border-slate-300 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-wider text-[#0B1221] transition-colors hover:border-[#0B1221]"
            >
              <FileUp className="h-4 w-4" strokeWidth={2.25} />
              Request Credential Update
            </button>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              data-testid="ac-cancel"
              onClick={onClose}
              className="flex-1 border border-slate-300 bg-white px-4 py-3 text-sm font-semibold uppercase tracking-wider text-[#0B1221] transition-colors hover:border-[#0B1221]"
            >
              {isEdit ? "Close" : "Cancel"}
            </button>
            <button
              type="submit"
              data-testid="ac-submit"
              disabled={submitting}
              className="flex-1 bg-[#FF5A1F] px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#e94d14] disabled:opacity-60"
            >
              {submitting ? "Saving…" : isEdit ? "Save Notes" : "Submit for Verification"}
            </button>
          </div>
        </form>
      </div>

      {showRequestUpdate && (
        <RequestCredentialUpdateModal
          credentialName={credential?.name}
          onClose={() => setShowRequestUpdate(false)}
        />
      )}
    </div>
  );
}
